import { useMapEvent } from "react-leaflet";
import React, { useState, useRef } from "react";
import earcut from "earcut";
import { useLanguage } from "./i18n";
import { useUser } from "./context/UserContext";

// Punkt-i-polygon-test (Ray casting, lng/lat)
function pointInPolygon(latlng, polygon) {
  const x = latlng.lat, y = latlng.lng;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1];
    const xj = polygon[j][0], yj = polygon[j][1];
    const intersect =
      ((yi > y) !== (yj > y)) &&
      (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function isLatLngInPolygon(latlng, polygonCoords) {
  const polyLatLng = polygonCoords.map(([lng, lat]) => [lat, lng]);
  return pointInPolygon({ lat: latlng.lat, lng: latlng.lng }, polyLatLng);
}

// Barycentrisk Z-interpolation
function barycentricZ(pA, pB, pC, mouseLatLng) {
  const [x1, y1, z1] = pA;
  const [x2, y2, z2] = pB;
  const [x3, y3, z3] = pC;
  const x = mouseLatLng.lng, y = mouseLatLng.lat;

  const detT = (y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3);
  if (Math.abs(detT) < 1e-12) return null;
  const l1 = ((y2 - y3) * (x - x3) + (x3 - x2) * (y - y3)) / detT;
  const l2 = ((y3 - y1) * (x - x3) + (x1 - x3) * (y - y3)) / detT;
  const l3 = 1 - l1 - l2;

  if (l1 < -0.0001 || l2 < -0.0001 || l3 < -0.0001) return null;
  return l1 * z1 + l2 * z2 + l3 * z3;
}

// Triangulera polygon med earcut
function triangulatePolygon(ring) {
  // ring: [[lng, lat, z], ...]
  const coords = [...ring];
  // Remove duplicate endpoint if exists
  if (
    coords.length > 1 &&
    coords[0][0] === coords[coords.length - 1][0] &&
    coords[0][1] === coords[coords.length - 1][1]
  ) {
    coords.pop();
  }
  const flat = [];
  for (const [lng, lat] of coords) {
    flat.push(lng, lat);
  }
  const triangles = [];
  const indices = earcut(flat);
  for (let i = 0; i < indices.length; i += 3) {
    triangles.push([
      coords[indices[i]],
      coords[indices[i + 1]],
      coords[indices[i + 2]],
    ]);
  }
  return triangles;
}

// Polygon: robust interpolerad Z i hela ytan (även konkav)
function getInterpolatedZOnPolygon(polygonCoords, mouseLatLng) {
  const ring = polygonCoords[0];
  if (!ring || !ring.length || typeof ring[0][2] !== "number") return null;
  const firstZ = ring[0][2];
  // Platt polygon: alla Z lika
  if (ring.every(coord => Math.abs(coord[2] - firstZ) < 1e-6)) {
    if (isLatLngInPolygon(mouseLatLng, ring)) {
      return firstZ;
    }
    return null;
  }
  // Triangulera hela polygonen
  const triangles = triangulatePolygon(ring);
  for (const tri of triangles) {
    const z = barycentricZ(tri[0], tri[1], tri[2], mouseLatLng);
    if (z !== null) return z;
  }
  return null;
}

// För multipolygon
function getInterpolatedZOnMultiPolygon(multiCoords, mouseLatLng) {
  for (let poly of multiCoords) {
    const z = getInterpolatedZOnPolygon([poly[0]], mouseLatLng);
    if (z !== null) return z;
  }
  return null;
}

// Koordinatformattering
function toDD(val) {
  return val.toFixed(7);
}
function toDDMM(val) {
  const d = Math.floor(Math.abs(val));
  const m = (Math.abs(val) - d) * 60;
  return `${val < 0 ? "-" : ""}${d}°${m.toFixed(5)}'`;
}
function toDDMMSS(val) {
  const d = Math.floor(Math.abs(val));
  const mFloat = (Math.abs(val) - d) * 60;
  const m = Math.floor(mFloat);
  const s = (mFloat - m) * 60;
  return `${val < 0 ? "-" : ""}${d}°${m}'${s.toFixed(4)}"`;
}
function formatCoord(val, format) {
  if (format === "ddmm") return toDDMM(val);
  if (format === "ddmmss") return toDDMMSS(val);
  return toDD(val);
}

export default function CoordinateInfoBox({ surfaces }) {
  const { t } = useLanguage();
  const { isAuthenticated, isPaid } = useUser();
  const [coords, setCoords] = useState(null);
  const [zValue, setZValue] = useState(null);
  const [format, setFormat] = useState("ddmmss");

  // Förhindra onödig setState-loop: använd refs för att lagra tidigare värden
  const prevCoordsRef = useRef();
  const prevZValueRef = useRef();

  // Determine if Z-value should be shown
  const canShowZValue = isAuthenticated && isPaid;

  useMapEvent("mousemove", e => {
    const newCoords = [e.latlng.lat, e.latlng.lng];
    let foundZ = null;
    const mouse = e.latlng;

    if (Array.isArray(surfaces)) {
      let surfaceZs = [];
      for (const surf of surfaces) {
        if (surf.geometry.type === "Polygon") {
          const z = getInterpolatedZOnPolygon(surf.geometry.coordinates, mouse);
          if (z !== null && z !== undefined) {
            surfaceZs.push(z);
          }
        } else if (surf.geometry.type === "MultiPolygon") {
          const z = getInterpolatedZOnMultiPolygon(surf.geometry.coordinates, mouse);
          if (z !== null && z !== undefined) {
            surfaceZs.push(z);
          }
        }
      }
      if (surfaceZs.length) {
        foundZ = Math.min(...surfaceZs);
      }
    }

    if (
      !prevCoordsRef.current ||
      prevCoordsRef.current[0] !== newCoords[0] ||
      prevCoordsRef.current[1] !== newCoords[1]
    ) {
      setCoords(newCoords);
      prevCoordsRef.current = newCoords;
    }
    if (prevZValueRef.current !== foundZ) {
      setZValue(foundZ);
      prevZValueRef.current = foundZ;
    }
  });

  return (
    <div className="coordinate-info-box">
      <div>
        <label>{t("coordinateInfo_format")} </label>
        <select value={format} onChange={e => setFormat(e.target.value)}>
          <option value="dd">dd.dddddd</option>
          <option value="ddmm">ddmm.mmmmmm</option>
          <option value="ddmmss">ddmmss.ssss</option>
        </select>
      </div>
      <div>
        <b>{t("coordinateInfo_lat")}</b> {coords ? formatCoord(coords[0], format) : "-"}
      </div>
      <div>
        <b>{t("coordinateInfo_lng")}</b> {coords ? formatCoord(coords[1], format) : "-"}
      </div>
      <div>
        <b>{t("coordinateInfo_z")}</b>{" "}
        {canShowZValue ? (
          zValue !== null && zValue !== undefined ? Number(zValue).toFixed(2) + " m" : "-"
        ) : (
          <span className="z-access-message">
            {t("coordinateInfo_zAccessMessage")}
          </span>
        )}
      </div>
    </div>
  );
}

// Exportera funktionerna så att andra komponenter kan använda Z-interpolering
export {
  getInterpolatedZOnPolygon,
  getInterpolatedZOnMultiPolygon
};